import React, { useEffect, useState } from 'react';
import { Form, Input, DatePicker, Select, Card, message, Space, Row, Col } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { insertData, getTableNextSequenceValue, fetchSqlData, updateData } from '../../../services/api';
import type { QaqcRfiForms } from '../../../types';
import PersonnelSelect from '../../../components/modals/PersonnelSelect';
import WPermitSelect from '../../../components/modals/WPermitSelect';
import { taskService } from '../../../components/common/TaskManagement/taskService';
import { useAuth } from '../../../components/common/security/AuthContext';
import { useGetPersonnel } from '../../../hooks/genericHooks';
import FileUploadTool, { FileUploadToolHandle } from '../../../components/common/FileUploadTool';
import FileDownloadTool from '../../../components/common/FileDownloadTool';
import Button from '../../../components/common/Button';

const { Option } = Select;
const { TextArea } = Input;

interface QaqcRfiFormCreateProps { mode?: 'create' | 'view' | 'edit' }

const QaqcRfiFormCreate: React.FC<QaqcRfiFormCreateProps> = ({ mode = 'create' }) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [persModalOpen, setPersModalOpen] = useState(false);
  const [wpModalOpen, setWpModalOpen] = useState(false);
  const [recModalOpen, setRecModalOpen] = useState(false);
  const params = useParams();
  const isView = mode === 'view';
  const isEdit = mode === 'edit';
  const { user } = useAuth();
  const { data: personnelData } = useGetPersonnel();
  const [currentStatus, setCurrentStatus] = useState<string | undefined>(undefined);
  const [loadingView, setLoadingView] = useState(false);
  const [formIdLabel, setFormIdLabel] = useState<string | number | undefined>(undefined);
  const [showAppointInvite, setShowAppointInvite] = useState(false);
  const [appModalOpen, setAppModalOpen] = useState(false);
  const [invModalOpen, setInvModalOpen] = useState(false);
  const [canEditView, setCanEditView] = useState(false);
  const location = useLocation();
  const fileUploadRef = React.useRef<FileUploadToolHandle>(null);
  const [uploadPath, setUploadPath] = useState<string>('');
  const [isTaskMarkedRead, setIsTaskMarkedRead] = useState(false);

  const taskNavState = (location as any)?.state as
    | { fromTask?: boolean; taskId?: number; purpose?: string }
    | undefined;
  const taskIdFromNav = taskNavState?.fromTask ? Number(taskNavState?.taskId) : NaN;
  const taskPurposeFromNav = taskNavState?.fromTask ? String(taskNavState?.purpose || '') : '';

  const markTaskAsRead = async () => {
    const actingPid = localStorage.getItem('personnel_id') || '';
    if (!actingPid) {
      message.warning('No personnel id in session. Please re-login.');
      return;
    }
    if (!Number.isFinite(taskIdFromNav)) return;
    try {
      await taskService.completeTaskById(taskIdFromNav, actingPid);
      message.success('Marked as read');
      setIsTaskMarkedRead(true);
    } catch {
      message.error('Failed to mark as read');
    }
  };

  // Mutation for creating RFI form
  const createRfiMutation = useMutation({
    mutationFn: (payload: Omit<QaqcRfiForms, 'id' | 'form_id' | 'created_at' | 'updated_at' |'created_by'|'updated_by'>) =>
      insertData('qaqc_rfi_forms', payload),
    onSuccess: () => {
      message.success('RFI form created successfully!');
      queryClient.invalidateQueries({ queryKey: ['qaqc_rfi_forms'] });
      navigate('/qaqc/ndt/pipe_inspections/rfi_list');
    },
    onError: (error: any) => {
    },
  });
  const onFinish = async (values: any) => {
    if (isView) return; // no-op in view mode
    // Determine issuer personnel_id from hidden field or by matching name
    const getIssuerPid = (): string | undefined => {
      const explicit = form.getFieldValue('issued_by_id');
      if (explicit) return String(explicit);
      const issuedByName: string | undefined = form.getFieldValue('issued_by');
      const list = (personnelData?.data || []) as any[];
      const match = issuedByName
        ? list.find((p: any) => `${p.first_name || ''} ${p.last_name || ''}`.trim() === issuedByName.trim())
        : undefined;
      return match ? String(match.pers_id || match.id || '') : undefined;
    };
    const issuerPid = getIssuerPid();
    const idsSet = new Set<string>(Array.isArray(values.recipient_ids) ? values.recipient_ids : []);
    if (issuerPid) idsSet.add(issuerPid);
    // Ensure creator is not auto-included unless they are the issuer
    const currentPid = localStorage.getItem('personnel_id') || '';
    if (currentPid && issuerPid !== currentPid) idsSet.delete(currentPid);
    const recipient_ids = Array.from(idsSet);
    const payload: Omit<QaqcRfiForms, 'id' | 'form_id' | 'created_at' | 'updated_at' | 'created_by' | 'updated_by'> = {
      work_permit_ref_num: values.work_permit_ref_num,
      work_permit_description: values.work_permit_description,
      issued_by: values.issued_by,
      request_date: values.request_date.format('YYYY-MM-DD'),
      rfi_subject: values.rfi_subject,
      rfi_notes: values.rfi_notes,
      rfi_status: values.rfi_status,
      recipients: values.recipients ?? [],
      recipient_ids,
      cancellation_reason: isEdit ? (values.cancellation_reason ?? null) : null,
      // Appointees / Invitees
      appointees: values.appointees ?? [],
      appointee_ids: values.appointee_ids ?? [],
      invitees: values.invitees ?? [],
      invitee_ids: values.invitee_ids ?? [],
    };

    // Update or Create
    if (isEdit) {
      const id = params.id ? Number(params.id) : NaN;
      if (!Number.isFinite(id)) return;

      // Optionally finalize file uploads before update to capture upload_path
      let nextUploadPath: string | null | undefined = undefined;
      try {
        if (fileUploadRef.current && fileUploadRef.current.hasFilesToUpload()) {
          const docRef = (formIdLabel && String(formIdLabel)) || `RFI-${id}`;
          const up = await fileUploadRef.current.finalizeUpload(docRef);
          if (!up.success) {
            message.error(up.error || 'File upload failed. Changes not saved.');
            return;
          }
          nextUploadPath = up.folderPath || null;
        }
      } catch {
        message.error('File upload failed. Changes not saved.');
        return;
      }

      await updateData('qaqc_rfi_forms', id, {
        ...payload,
        ...(nextUploadPath !== undefined ? { upload_path: nextUploadPath } : {}),
      });

      if (nextUploadPath !== undefined) {
        setUploadPath(nextUploadPath || '');
      }
      // After successful update, create tasks for appointees and invitees if present
      try {
        const subject = values.rfi_subject || '';
        const descBase = `${subject} - ${values.issued_by || ''} - ${values.request_date?.format?.('YYYY-MM-DD') || values.request_date || ''}`.trim();
        const formId = formIdLabel;
        const base = formId ? `RFI ${formId}: ` : 'RFI: ';
        const viewPath = `/qaqc/rfi/view/${id}`;
        const appointeeIds: string[] = Array.isArray(values.appointee_ids) ? values.appointee_ids : [];
        const inviteeIds: string[] = Array.isArray(values.invitee_ids) ? values.invitee_ids : [];
        if (appointeeIds.length) {
          await taskService.notify({
            entity: { kind: 'qaqc.rfi', id },
            assignees: appointeeIds,
            navigateTo: viewPath,
            description: `${base}${descBase} (Appointed)`,
            tag: 'appointed',
            clientKey: `rfi:${id}:appointed`,
            fanoutPerAssignee: false,
          });
        }
        if (inviteeIds.length) {
          await taskService.notify({
            entity: { kind: 'qaqc.rfi', id },
            assignees: inviteeIds,
            navigateTo: viewPath,
            description: `${base}${descBase} (Invited)`,
            tag: 'invited',
            clientKey: `rfi:${id}:invited`,
            fanoutPerAssignee: false,
          });
        }
      } catch {}
      await queryClient.invalidateQueries({ queryKey: ['qaqc_rfi_forms'] });
      message.success('RFI form updated');
      navigate('/qaqc/rfi');
      return;
    }

    // Create mode
    // Preflight: if files staged, ensure destination folder exists for this route (strict)
    if (fileUploadRef.current && fileUploadRef.current.hasFilesToUpload()) {
      const cleanPath = (path: string) => {
        // Strip trailing dynamic id when path is .../(create|edit|view)/:id
        let p = path;
        const parts = p.split('/').filter(Boolean);
        if (parts.length >= 2) {
          const prev = parts[parts.length - 2];
          if (prev === 'edit' || prev === 'view' || prev === 'create') {
            parts.pop();
            p = '/' + parts.join('/');
          }
        }
        return p;
      };
      const primary = cleanPath(location.pathname);
      console.log('[RFI Create] Preflight folder check (strict)', { pathname: location.pathname, primary });
      try {
        const dest = await fetchSqlData('cor_destination_folders', 'route_path', primary);
        const rows = Array.isArray((dest as any)?.data) ? (dest as any).data : (Array.isArray(dest) ? dest : []);
        console.log('[RFI Create] Destination folder query result', { pathTried: primary, rows });
        if (!Array.isArray(rows) || rows.length === 0) {
          message.error('No destination folder configured for this page. Please contact admin.');
          return;
        }
      } catch {
        message.error('Failed to verify destination folder. Please retry.');
        return;
      }
    }

    const predictedId = await getTableNextSequenceValue('qaqc_rfi_forms', 'id');
    createRfiMutation.mutate(payload, {
      onSuccess: async (res: any) => {
        try {
          const returnedId = res?.inserted_id ?? res?.id ?? res?.[0]?.id ?? res?.data?.id;
          const formRecordId = Number(returnedId ?? predictedId);
          // Finalize uploads and persist upload_path if files were staged
          if (fileUploadRef.current && fileUploadRef.current.hasFilesToUpload() && Number.isFinite(formRecordId)) {
            const docRef = (formIdLabel && String(formIdLabel)) || `RFI-${formRecordId}`;
            console.log('[RFI Create] Finalize upload starting', { docRef });
            const up = await fileUploadRef.current.finalizeUpload(docRef);
            console.log('[RFI Create] Finalize upload result', up);
            if (!up.success) {
              message.error(up.error || 'File upload failed');
              return;
            }
            const upd = { upload_path: up.folderPath || null } as Partial<QaqcRfiForms>;
            console.log('[RFI Create] Updating upload_path', { id: formRecordId, upd });
            await updateData('qaqc_rfi_forms', formRecordId, upd);
          }
          const assignedCodes: string[] = recipient_ids;
          if (formRecordId && assignedCodes.length) {
            let formIdLabel: string | number | undefined = undefined;
            try {
              const fetchRes = await fetchSqlData('qaqc_rfi_forms', 'id', formRecordId);
              const createdRow = Array.isArray(fetchRes?.data) ? fetchRes.data[0] : (Array.isArray(fetchRes) ? fetchRes[0] : null);
              formIdLabel = createdRow?.form_id;
            } catch {}
            const descBase = `${values.rfi_subject || ''} - ${values.issued_by || ''} - ${values.request_date?.format('YYYY-MM-DD') || ''}`.trim();
            const desc = (formIdLabel ? `RFI ${formIdLabel}: ` : 'RFI: ') + descBase;
            const client_key = `rfi:${formRecordId}:notify`;
            await taskService.notify({
              entity: { kind: 'qaqc.rfi', id: formRecordId },
              assignees: assignedCodes,
              navigateTo: `/qaqc/rfi/view/${formRecordId}`,
              description: desc,
              tag: 'notify',
              clientKey: client_key,
              fanoutPerAssignee: false,
            });
          }
          // Ensure list refresh
          queryClient.invalidateQueries({ queryKey: ['qaqc_rfi_forms'] });
        } catch {}
      },
    });
  };

  // View/Edit: fetch and fill existing record
  useEffect(() => {
    const load = async () => {
      if (!isView && !isEdit) return;
      const id = params.id ? Number(params.id) : NaN;
      if (!Number.isFinite(id)) return;
      setLoadingView(true);
      try {
        const res = await fetchSqlData('qaqc_rfi_forms', 'id', id);
        const row = Array.isArray(res?.data) ? res.data[0] : (Array.isArray(res) ? res[0] : null);
        if (row) {
          // Try to resolve issuer personnel id from name using personnel list
          let issuerPidResolved: string | undefined = undefined;
          try {
            const list = (personnelData?.data || []) as any[];
            if (Array.isArray(list) && row.issued_by) {
              const m = list.find((p: any) => `${p.first_name || ''} ${p.last_name || ''}`.trim() === String(row.issued_by).trim());
              issuerPidResolved = m ? String(m.pers_id || m.id || '') : undefined;
            }
          } catch {}
          form.setFieldsValue({
            work_permit_ref_num: row.work_permit_ref_num ?? undefined,
            work_permit_description: row.work_permit_description ?? '',
            issued_by: row.issued_by ?? undefined,
            issued_by_id: issuerPidResolved,
            request_date: row.request_date ? dayjs(row.request_date) : undefined,
            rfi_subject: row.rfi_subject ?? undefined,
            rfi_notes: row.rfi_notes ?? undefined,
            rfi_status: row.rfi_status ?? 'draft',
            recipients: row.recipients ?? [],
            recipient_ids: row.recipient_ids ?? [],
            recipients_display: Array.isArray(row.recipients) ? row.recipients.join('; ') : undefined,
            cancellation_reason: row.cancellation_reason ?? undefined,
            // Appointees / Invitees
            appointees: row.appointees ?? [],
            appointee_ids: row.appointee_ids ?? [],
            invitees: row.invitees ?? [],
            invitee_ids: row.invitee_ids ?? [],
            appointees_display: Array.isArray(row.appointees) ? row.appointees.join('; ') : undefined,
            invitees_display: Array.isArray(row.invitees) ? row.invitees.join('; ') : undefined,
          });
          setCurrentStatus(row.rfi_status);
          setFormIdLabel(row.form_id);
          setUploadPath(row.upload_path || '');

          // In view mode, decide if Edit button should be shown
          if (isView) {
            try {
              const currentPid = localStorage.getItem('personnel_id') || '';
              const recipientIds: string[] = Array.isArray(row.recipient_ids) ? row.recipient_ids : [];
              const notCancelled = String(row.rfi_status || '').toLowerCase() !== 'cancelled';
              setCanEditView(Boolean(currentPid && recipientIds.includes(currentPid) && notCancelled));
            } catch {
              setCanEditView(false);
            }
          }

          // Edit permission guard
          if (isEdit) {
            const currentPid = localStorage.getItem('personnel_id') || '';
            const recipientIds: string[] = Array.isArray(row.recipient_ids) ? row.recipient_ids : [];
            const isIssuer = !!row.created_by && String(row.created_by) === currentPid;
            const isRecipient = currentPid ? recipientIds.includes(currentPid) : false;
            const canEdit = isIssuer || isRecipient;
            if (!canEdit) {
              message.warning('You are not allowed to edit this RFI. Redirected to view.');
              navigate(`/qaqc/rfi/view/${id}`);
              return;
            }
          }
        }
      } finally {
        setLoadingView(false);
      }
    };
    load();
  }, [isView, isEdit, params.id, form, navigate]);
  

  // On create: prefill issuer name and ensure issuer id is in recipient_ids
  useEffect(() => {
    if (isView || isEdit) return;
    const currentPid = localStorage.getItem('personnel_id') || '';
    if (!personnelData || !Array.isArray(personnelData.data)) return;
    const list = personnelData.data as any[];
    const me = list.find((p: any) => String(p.pers_id || '') === currentPid || String(p.id || '') === currentPid);
    const fullName = me ? `${me.first_name || ''} ${me.last_name || ''}`.trim() : undefined;
    form.setFieldsValue({
      issued_by: fullName || form.getFieldValue('issued_by'),
      issued_by_id: currentPid || form.getFieldValue('issued_by_id'),
    });
  }, [isView, isEdit, personnelData, form]);

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>
            {(isView || isEdit) && (
              <a onClick={() => navigate(-1)} style={{ marginRight: 16, fontSize: 16 }}>&larr; Back</a>
            )}
            {isView ? 'View RFI Form' : isEdit ? 'Edit RFI Form' : 'Create RFI Form'}
            {(isView || isEdit) && formIdLabel ? ` — ${formIdLabel}` : ''}
          </span>
          <Space>
            {isView && !isTaskMarkedRead && Number.isFinite(taskIdFromNav) && (
              <Button type="primary" onClick={markTaskAsRead}>
                Mark as read
              </Button>
            )}
            {isView && canEditView && (
              <Button action="edit" type="primary" onClick={() => {
                const id = params.id ? Number(params.id) : NaN;
                if (Number.isFinite(id)) navigate(`/qaqc/rfi/edit/${id}`);
              }}>
                Edit
              </Button>
            )}
          </Space>
        </div>
      }
      style={{ maxWidth: 600, margin: '0 auto' }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ rfi_status: 'draft', request_date: dayjs(), cancellation_reason: null }}
        size="small"
      >
        {/* Hidden helper for issuer personnel id */}
        <Form.Item name="issued_by_id" hidden>
          <Input type="hidden" />
        </Form.Item>
        {/* Hidden helpers for appointee/invitee ids and arrays */}
        <Form.Item name="appointees" hidden>
          <Select mode="tags" open={false} style={{ display: 'none' }} />
        </Form.Item>
        <Form.Item name="appointee_ids" hidden>
          <Select mode="tags" open={false} style={{ display: 'none' }} />
        </Form.Item>
        <Form.Item name="invitees" hidden>
          <Select mode="tags" open={false} style={{ display: 'none' }} />
        </Form.Item>
        <Form.Item name="invitee_ids" hidden>
          <Select mode="tags" open={false} style={{ display: 'none' }} />
        </Form.Item>
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item label={<b>Issued By</b>} required style={{ marginBottom: 8 }}>
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item name="issued_by" noStyle rules={[{ required: true, message: 'Please select the issuer' }]}>
                  <Input readOnly placeholder="Select personnel" />
                </Form.Item>
                {!isView && !isEdit && (
                  <Button onMouseDown={(e) => e.preventDefault()} onClick={() => setPersModalOpen(true)}>Select</Button>
                )}
              </Space.Compact>
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={<b>Request Date</b>}
              name="request_date"
              rules={[{ required: true, message: 'Please select the request date' }]}
              style={{ marginBottom: 8 }}
            >
              <DatePicker style={{ width: '100%' }} disabled={isView} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label={<b>Recipients (send to)</b>} required style={{ marginBottom: 8 }}>
          <Space.Compact style={{ width: '100%' }}>
            <Form.Item name="recipients_display" noStyle rules={[{ required: true, message: 'Please select at least one recipient' }]}>
              <TextArea readOnly placeholder="Select one or more personnel" autoSize={{ minRows: 1, maxRows: 6 }} />
            </Form.Item>
            <Form.Item name="recipients" hidden>
              <Select mode="tags" open={false} style={{ display: 'none' }} />
            </Form.Item>
            <Form.Item name="recipient_ids" hidden>
              <Select mode="tags" open={false} style={{ display: 'none' }} />
            </Form.Item>
            {!isView && (
              <Button onMouseDown={(e) => e.preventDefault()} onClick={() => setRecModalOpen(true)}>Select</Button>
            )}
          </Space.Compact>
        </Form.Item>

        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Form.Item label={<b>Work Permit Ref No.</b>} required style={{ marginBottom: 8 }}>
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item name="work_permit_ref_num" noStyle rules={[{ required: true, message: 'Please enter the Work Permit reference number' }]}> 
                  <Input readOnly placeholder="e.g. WP-2025-00123" />
                </Form.Item>
                {!isView && (
                  <Button
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setWpModalOpen(true)}
                    disabled={isEdit && currentStatus !== 'draft'}
                  >
                    Select
                  </Button>
                )}
              </Space.Compact>
            </Form.Item>
          </Col>
          <Col xs={24} md={16}>
            <Form.Item
              label={<b>Work Permit Description</b>}
              name="work_permit_description"
              rules={[{ required: true, message: 'Work Permit Description is required' }]}
              style={{ marginBottom: 8 }}
            >
              <Input readOnly placeholder="Auto-filled from Work Permit selection" />
            </Form.Item>
          </Col>
        </Row>

        {/* Inline Subject label + input on the same line (with required star) */}
        <Form.Item style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ minWidth: 90, fontWeight: 600 }}>
              Subject <span style={{ color: '#ff4d4f' }}>*</span>
            </span>
            <Form.Item
              name="rfi_subject"
              noStyle
              rules={[{ required: true, message: 'Please enter the subject' }]}
            >
              <Input readOnly={isView} style={{ flex: 1 }} />
            </Form.Item>
          </div>
        </Form.Item>

        <Form.Item
          label={<b>Notes</b>}
          name="rfi_notes"
          style={{ marginBottom: 8 }}
        >
          <TextArea rows={3} readOnly={isView} />
        </Form.Item>

        {/* Inline Status + Show/Hide Appoint/Invite on the same line */}
        <Form.Item style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ minWidth: 90, fontWeight: 600 }}>Status</span>
            <Form.Item
              name="rfi_status"
              noStyle
              rules={[{ required: true, message: 'Please select the status' }]}
            >
              <Select disabled={isView} style={{ minWidth: 160 }}>
                <Option value="draft">Draft</Option>
                <Option value="active">Active</Option>
                <Option value="archived">Archived</Option>
                <Option value="cancelled">Cancelled</Option>
              </Select>
            </Form.Item>
            {isEdit && (
              <Button
                type="primary"
                style={{ backgroundColor: '#faad14', borderColor: '#faad14', marginLeft: 'auto' }}
                onClick={() => setShowAppointInvite(v => !v)}
              >
                {showAppointInvite ? 'Hide Appoint/Invite' : 'Show Appoint/Invite'}
              </Button>
            )}
          </div>
        </Form.Item>

        {/* Show cancellation reason only when editing and status is cancelled */}
        <Form.Item noStyle shouldUpdate={(prev, cur) => prev.rfi_status !== cur.rfi_status}>
          {({ getFieldValue }) => {
            const status = getFieldValue('rfi_status');
            return (isEdit && status === 'cancelled') ? (
              <Form.Item
                label={<b>Cancellation Reason</b>}
                name="cancellation_reason"
                rules={[{ required: true, message: 'Please provide a cancellation reason' }]}
                style={{ marginBottom: 8 }}
              >
                <TextArea rows={3} />
              </Form.Item>
            ) : null;
          }}
        </Form.Item>

        {/* Read-only cancellation reason on View when status is cancelled */}
        <Form.Item noStyle shouldUpdate={(prev, cur) => prev.rfi_status !== cur.rfi_status || prev.cancellation_reason !== cur.cancellation_reason}>
          {({ getFieldValue }) => {
            const status = getFieldValue('rfi_status');
            const reason = getFieldValue('cancellation_reason');
            return (isView && status === 'cancelled' && reason) ? (
              <Form.Item
                label={<b>Cancellation Reason</b>}
                name="cancellation_reason"
                style={{ marginBottom: 8 }}
              >
                <TextArea rows={3} readOnly />
              </Form.Item>
            ) : null;
          }}
        </Form.Item>

        {/* Appoint/Invite inputs (Edit mode) */}

        {isEdit && showAppointInvite && (
          <Form.Item label={<b>Appointees</b>} style={{ marginBottom: 8 }}>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item name="appointees_display" noStyle>
                <TextArea readOnly placeholder="Select appointees" autoSize={{ minRows: 1, maxRows: 6 }} />
              </Form.Item>
              <Button onMouseDown={(e) => e.preventDefault()} onClick={() => setAppModalOpen(true)}>Select</Button>
            </Space.Compact>
          </Form.Item>
        )}

        {isEdit && showAppointInvite && (
          <Form.Item label={<b>Invitees</b>} style={{ marginBottom: 8 }}>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item name="invitees_display" noStyle>
                <TextArea readOnly placeholder="Select invitees" autoSize={{ minRows: 1, maxRows: 6 }} />
              </Form.Item>
              <Button onMouseDown={(e) => e.preventDefault()} onClick={() => setInvModalOpen(true)}>Select</Button>
            </Space.Compact>
          </Form.Item>
        )}

        {/* Read-only Appointees/Invitees on View if populated */}
        {isView && (
          <>
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.appointees !== cur.appointees || prev.invitees !== cur.invitees}>
              {({ getFieldValue }) => {
                const app = getFieldValue('appointees') || [];
                const inv = getFieldValue('invitees') || [];
                return (
                  <>
                    {Array.isArray(app) && app.length > 0 && (
                      <Form.Item label={<b>Appointees</b>} style={{ marginBottom: 8 }}>
                        <TextArea rows={2} readOnly value={app.join('; ')} />
                      </Form.Item>
                    )}
                    {Array.isArray(inv) && inv.length > 0 && (
                      <Form.Item label={<b>Invitees</b>} style={{ marginBottom: 8 }}>
                        <TextArea rows={2} readOnly value={inv.join('; ')} />
                      </Form.Item>
                    )}
                  </>
                );
              }}
            </Form.Item>
          </>
        )}

        {!isView && (
          <Form.Item style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {isEdit && (<FileUploadTool ref={fileUploadRef} buttonText="Upload Files" />)}
              {isEdit && uploadPath && uploadPath.trim() !== '' && (
                <FileDownloadTool
                  buttonText={(<Button>Attached Files</Button>)}
                  contentPath={uploadPath}
                />
              )}
              {!isEdit && <FileUploadTool ref={fileUploadRef} buttonText="Upload Files" />}
              {isEdit ? (
                <Button action="edit" type="primary" htmlType="submit">Save Changes</Button>
              ) : (
                <Button action="add" type="primary" htmlType="submit" loading={createRfiMutation.isPending}>Create RFI</Button>
              )}
            </div>
          </Form.Item>
        )}
      </Form>

      {/* View-mode attachments */}
      {isView && uploadPath && uploadPath.trim() !== '' && (
        <div style={{ marginTop: 12 }}>
          <FileDownloadTool
            buttonText={(<Button>Attached Files</Button>)}
            contentPath={uploadPath}
          />
        </div>
      )}

      {!isView && !isEdit && (
        <PersonnelSelect
          open={persModalOpen}
          onCancel={() => setPersModalOpen(false)}
          onOk={(val: any) => {
            let fullName: string | undefined;
            let pid: string | undefined;
            if (Array.isArray(val)) {
              // Expecting [pid, code?, name]
              fullName = val[2];
              pid = String(val[0] ?? '');
            } else if (typeof val === 'string') {
              // Try to parse "code - name" or resolve by name from personnel list
              const s = val.trim();
              const dash = s.indexOf(' - ');
              if (dash >= 0) {
                pid = s.slice(0, dash).trim();
                fullName = s.slice(dash + 3).trim();
              } else {
                fullName = s;
                try {
                  const list = (personnelData?.data || []) as any[];
                  const match = list.find((p: any) => `${p.first_name || ''} ${p.last_name || ''}`.trim() === s);
                  if (match) pid = String(match.pers_id || match.id || '');
                } catch {}
              }
            }
            form.setFieldsValue({
              issued_by: fullName || form.getFieldValue('issued_by'),
              issued_by_id: pid || form.getFieldValue('issued_by_id'),
            });
            setPersModalOpen(false);
          }}
          mode="name_only"
        />
      )}

      {!isView && (
        <PersonnelSelect
          open={recModalOpen}
          onCancel={() => setRecModalOpen(false)}
          onOk={(val: any) => {
            const arr = Array.isArray(val) ? val : [val];
            const names: string[] = [];
            const codes: string[] = [];
            arr.forEach((s: any) => {
              const str = String(s);
              const idx = str.indexOf(' - ');
              if (idx >= 0) {
                codes.push(str.slice(0, idx).trim());
                names.push(str.slice(idx + 3).trim());
              } else {
                names.push(str.trim());
              }
            });
            // Ensure issuer (from Issued By) is included in recipient_ids
            const issuerPid: string | undefined = form.getFieldValue('issued_by_id')
              || (() => {
                const issuedByName: string | undefined = form.getFieldValue('issued_by');
                const list = (personnelData?.data || []) as any[];
                const match = issuedByName
                  ? list.find((p: any) => `${p.first_name || ''} ${p.last_name || ''}`.trim() === issuedByName.trim())
                  : undefined;
                return match ? String(match.pers_id || match.id || '') : undefined;
              })();
            if (issuerPid && !codes.includes(issuerPid)) codes.push(issuerPid);
            const display = names.join('; ');
            form.setFieldsValue({ recipients: names, recipient_ids: codes, recipients_display: display });
            setRecModalOpen(false);
          }}
          multi
        />
      )}

      {!isView && (
        <WPermitSelect
          open={wpModalOpen}
          value={form.getFieldValue('work_permit_ref_num')}
          onCancel={() => setWpModalOpen(false)}
          onOk={([refNum, desc]) => {
            form.setFieldsValue({ work_permit_ref_num: refNum, work_permit_description: desc });
            setWpModalOpen(false);
          }}
        />
      )}

      {/* Appointees selector modal */}
      {isEdit && (
        <PersonnelSelect
          open={appModalOpen}
          onCancel={() => setAppModalOpen(false)}
          onOk={(val: any) => {
            const arr = Array.isArray(val) ? val : [val];
            const names: string[] = [];
            const codes: string[] = [];
            arr.forEach((s: any) => {
              const str = String(s);
              let pid: string | undefined;
              let nameStr: string = str;
              const idx = str.indexOf(' - ');
              if (idx >= 0) {
                pid = str.slice(0, idx).trim();
                nameStr = str.slice(idx + 3).trim();
              }
              // Resolve pid by name if not provided
              if (!pid) {
                try {
                  const list = (personnelData?.data || []) as any[];
                  const rec = list.find((p: any) => `${p.first_name || ''} ${p.last_name || ''}`.trim() === nameStr);
                  if (rec) pid = String(rec.pers_id || rec.id || '');
                } catch {}
              }
              const companyCode = pid ? String(pid).slice(0, 3) : '';
              const display = companyCode ? `${nameStr} (${companyCode})` : nameStr;
              if (pid) codes.push(pid);
              names.push(display);
            });
            const display = names.join('; ');
            const setVals: any = { appointees: names, appointee_ids: codes, appointees_display: display };
            if (isEdit && codes.length > 0) {
              setVals.rfi_status = 'active';
            }
            form.setFieldsValue(setVals);
            setAppModalOpen(false);
          }}
          multi
        />
      )}

      {/* Invitees selector modal */}
      {isEdit && (
        <PersonnelSelect
          open={invModalOpen}
          onCancel={() => setInvModalOpen(false)}
          onOk={(val: any) => {
            const arr = Array.isArray(val) ? val : [val];
            const names: string[] = [];
            const codes: string[] = [];
            arr.forEach((s: any) => {
              const str = String(s);
              let pid: string | undefined;
              let nameStr: string = str;
              const idx = str.indexOf(' - ');
              if (idx >= 0) {
                pid = str.slice(0, idx).trim();
                nameStr = str.slice(idx + 3).trim();
              }
              // Resolve pid by name if not provided
              if (!pid) {
                try {
                  const list = (personnelData?.data || []) as any[];
                  const rec = list.find((p: any) => `${p.first_name || ''} ${p.last_name || ''}`.trim() === nameStr);
                  if (rec) pid = String(rec.pers_id || rec.id || '');
                } catch {}
              }
              const companyCode = pid ? String(pid).slice(0, 3) : '';
              const display = companyCode ? `${nameStr} (${companyCode})` : nameStr;
              if (pid) codes.push(pid);
              names.push(display);
            });
            const display = names.join('; ');
            const setVals: any = { invitees: names, invitee_ids: codes, invitees_display: display };
            if (isEdit && codes.length > 0) {
              setVals.rfi_status = 'active';
            }
            form.setFieldsValue(setVals);
            setInvModalOpen(false);
          }}
          multi
        />
      )}
    </Card>
  );
};

export default QaqcRfiFormCreate;

